import { supportsResearchInputKind, } from './product-research-inputs.js';
import { publicShopifyTestType } from './shopify-comparison-type.js';
/** Public test types this Opportunity workflow currently supports. */
export const OPPORTUNITY_ENABLED_KINDS = [
    'description',
    'claim',
    'price',
];
/** Experiments that can run today. A rule needing another type gives "No test available yet" (#4136). */
export const SUPPORTED_EXPERIMENT_TYPES = ['product_page_message'];
/** Discovery constraints, not fixed questions or a claim that any finding implies a useful test. */
export const OPPORTUNITY_FINDING_RULES = {
    recurrence: {
        meaning: 'Customers observed buying this product on more than one qualifying paid order.',
        prohibitedClaims: [
            'Observed motives, a deficient product page, replenishment or subscription demand.',
            'Simulated respondents are actual past buyers.',
        ],
    },
    pair: {
        meaning: 'The canonical pair appeared together in collected qualifying paid baskets.',
        prohibitedClaims: [
            'Co-purchase alone establishes compatibility, shared use or bundle demand.',
            'Changing either product causes basket growth.',
        ],
    },
    next_order: {
        meaning: 'Earlier and next products in the next observed qualifying paid order, in that direction.',
        prohibitedClaims: [
            'First-ever purchases, complete sequences or optimal reminder timing.',
            'The earlier product caused the transition or predicts buying the next product.',
        ],
    },
};
/** Current recurrence-only preparation recipe. Broader discovery does not make its single-pick executor relational. */
export const OPPORTUNITY_INSIGHT_RULES = {
    'product-history': {
        id: 'product-history->bring-buyers-back',
        // v4: public test types only; usage and other copy classifications map to Claim.
        version: 4,
        goal: null,
        researchQuestion: {
            id: 'product_page.supported_comparison',
            text: 'Is there a supported product-page message or price comparison worth investigating?',
        },
        respondentFollowUp: 'What does this product appear to offer, and what, if anything, is unclear?',
        // Owner, 2026-10-28: the public types this workflow can execute today.
        allowedKinds: ['claim', 'description', 'price'],
        experimentType: 'product_page_message',
        strongerLevers: [],
    },
};
/** Supported constructs, not a semantic quality score. Prices cannot be relabelled as copy tests. */
export const OPPORTUNITY_QUESTIONS = {
    message_understanding: 'How do these product-page versions differ in what shoppers understand and find appealing?',
    price_response: 'How do these price versions affect shoppers’ impressions and stated purchase interest?',
};
export function opportunityQuestionIdForKind(kind) {
    return kind === 'price' ? 'price_response' : 'message_understanding';
}
export function isSupportedExperimentType(type) {
    return SUPPORTED_EXPERIMENT_TYPES.includes(type);
}
/** The product's testable fields of the rule's kinds, each with its meanings, their layer and its value fingerprint. */
export function shortlistOpportunityFields(rule, fields, meaningOf) {
    return fields.flatMap((field) => {
        if (!field.classification.eligibleForStudy || field.classification.hidden)
            return [];
        const classifiedTypes = new Set(field.classification.marketingVariantTypes.map((type) => publicShopifyTestType(type)));
        const kinds = rule.allowedKinds.filter((kind) => classifiedTypes.has(kind) && supportsResearchInputKind(field, kind));
        if (kinds.length === 0)
            return [];
        return [
            {
                researchInputId: field.source.metafieldId,
                namespace: field.source.namespace,
                key: field.source.key,
                variantId: field.source.variantId ?? null,
                label: field.label,
                kinds,
                ...meaningOf(field),
                valueFingerprint: field.source.valueFingerprint,
            },
        ];
    });
}
//# sourceMappingURL=opportunity-insight-rules.js.map